import { IconButton, Icons } from '@commenda/kakashi';
import { NonTimezoneSensitiveDate } from '@commenda/utils/non-time-zone-sensitive-date';
import { ChangelogEntry } from './types';
import { renderNodes } from './utils';

/** ISO 8601 date part (YYYY-MM-DD). Accepts date-only or datetime (e.g. with T...Z). */
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function formatChangelogDate(dateString: string | undefined | null): string {
  if (dateString == null || dateString === '') return '';
  const datePart = dateString.includes('T') ? dateString.split('T')[0] : dateString.trim();
  if (datePart === '' || !ISO_DATE_REGEX.test(datePart)) {
    return dateString.trim() !== '' ? dateString : '';
  }
  return NonTimezoneSensitiveDate.fromISO8601String(datePart).toReadableString();
}

export function DetailView({
  entry,
  onBack,
}: {
  entry: ChangelogEntry;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col max-h-[min(calc(100vh-80px),680px)]">
      {/* Header — sticky with back button */}
      <div className="shrink-0 border-b border-border-primary bg-bg-light rounded-t-lg">
        <div className="mx-5 mt-5 pb-4">
          <div className="flex h-7 items-center gap-1.5">
            <IconButton
              size="md"
              icon={
                <Icons.ChevronLeft className="size-5 text-text-secondary" />
              }
              aria-label="Back to changelog list"
              onClick={onBack}
            />
            <h2 className="ml-0.5 text-lg font-semibold text-text-primary select-none truncate whitespace-nowrap">
              Changelog
            </h2>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="overflow-y-auto flex-1">
        <div className="px-8 py-6">
          {/* Date */}
          <div className="text-sm text-text-tertiary">
            {formatChangelogDate(entry.date)}
          </div>

          {/* Title */}
          <h2 className="mt-2 text-2xl font-semibold text-text-primary">
            {entry.title}
          </h2>

          {/* Cover image */}
          {entry.imageUrl && (
            <div className="my-4 overflow-hidden rounded-md border border-border-primary shadow-sm">
              <img
                src={entry.imageUrl}
                alt={entry.title}
                className="w-full object-cover"
                loading="lazy"
              />
            </div>
          )}

          {/* Rich text content */}
          {entry.notes?.content && (
            <div className="mt-4 text-sm">
              {renderNodes(entry.notes.content)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
